<template>
  <CustomTitleBar title="忘记密码" />
  <div class="reset-pwd-container">
    <!-- 手机号输入框 -->
    <div class="input-group">
      <van-field v-model="phoneNumber" placeholder="输入手机号" :border="false">
        <template #label>
          <div class="country-code" @click="showAreaCodePicker = true">
            <SvgIcon name="phone" />
            <span>+{{ phoneAreaPrefix }}</span>
            <van-icon name="arrow-down" size="14" color="#666666" />
          </div>
        </template>
      </van-field>
    </div>

    <!-- 新密码输入框 -->
    <div class="input-group">
      <van-field v-model="password" :type="showPassword ? 'text' : 'password'" placeholder="输入新密码" :border="false">
        <template #label>
          <SvgIcon name="pwd" />
        </template>
        <template #right-icon>
          <van-icon :name="showPassword ? 'eye-o' : 'closed-eye'" @click="togglePasswordVisibility" />
        </template>
      </van-field>
    </div>

    <!-- 确认密码输入框 -->
    <div class="input-group">
      <van-field v-model="confirmPassword" :type="showConfirmPassword ? 'text' : 'password'" placeholder="确认新密码"
        :border="false">
        <template #label>
          <SvgIcon name="pwd" />
        </template>
        <template #right-icon>
          <van-icon :name="showConfirmPassword ? 'eye-o' : 'closed-eye'" @click="toggleConfirmPasswordVisibility" />
        </template>
      </van-field>
    </div>

    <!-- 验证码输入框 -->
    <div class="input-group">
      <van-field v-model="verificationCode" placeholder="输入验证码" :border="false">
        <template #label>
          <SvgIcon name="code2" />
        </template>
        <template #right-icon>
          <van-button size="small" plain type="primary" @click="sendVerificationCode" :disabled="cooldownTime > 0">
            {{ cooldownTime > 0 ? `${cooldownTime}s` : '获取验证码' }}
          </van-button>
        </template>
      </van-field>
    </div>

    <!-- 确认修改按钮 -->
    <van-button type="primary" block round @click="submitForm">确认修改</van-button>

    <!-- 区号选择器弹出层 -->
    <van-popup v-model:show="showAreaCodePicker" position="bottom">
      <van-picker :columns="areaCodeColumns" @confirm="onAreaCodeConfirm" @cancel="showAreaCodePicker = false"
        :title="'选择区号'" show-toolbar />
    </van-popup>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { showToast } from 'vant'
import { useRouter } from 'vue-router'
import { sendSmsCode, forgetPwdApi } from '@/api/system'
import CustomTitleBar from "@/components/CustomTitleBar/index.vue"

const router = useRouter()

// 表单数据
const phoneAreaPrefix = ref('86')
const phoneNumber = ref('')
const password = ref('')
const confirmPassword = ref('')
const verificationCode = ref('')
const showPassword = ref(false)
const showConfirmPassword = ref(false)
const showAreaCodePicker = ref(false)
const cooldownTime = ref(0)
let timer: ReturnType<typeof setInterval> | null = null

// 区号数据
const areaCodeColumns = [
  { text: '中国 +86', value: '86' },
  { text: '中国香港 +852', value: '852' },
  { text: '中国澳门 +853', value: '853' },
  { text: '中国台湾 +886', value: '886' },
  { text: '日本 +81', value: '81' },
  { text: '韩国 +82', value: '82' },
  { text: '美国 +1', value: '1' },
]

// 表单验证
const validateForm = () => {
  if (!phoneNumber.value) {
    showToast('请输入手机号')
    return false
  }
  if (!/^1[3-9]\d{9}$/.test(phoneNumber.value)) {
    showToast('请输入正确的手机号')
    return false
  }
  if (!password.value) {
    showToast('请输入新密码')
    return false
  }
  if (password.value.length < 6) {
    showToast('密码不能少于6位')
    return false
  }
  if (password.value !== confirmPassword.value) {
    showToast('两次输入的密码不一致')
    return false
  }
  if (!verificationCode.value) {
    showToast('请输入验证码')
    return false
  }
  return true
}

// 发送验证码
const sendVerificationCode = async () => {
  if (!phoneNumber.value) {
    showToast('请输入手机号')
    return
  }
  if (cooldownTime.value > 0) return

  try {
    const res = await sendSmsCode({
      phoneAreaPrefix: phoneAreaPrefix.value,
      phoneNumber: phoneNumber.value,
      type: 2
    })

    if (res.code === 200) {
      showToast('验证码发送成功')
      cooldownTime.value = 60
      timer = setInterval(() => {
        cooldownTime.value--
        if (cooldownTime.value <= 0 && timer) {
          clearInterval(timer)
          timer = null
        }
      }, 1000)
    } else {
      showToast(res.msg)
    }
  } catch (error) {
    console.error(error)
  }
}

// 提交表单
const submitForm = async () => {
  if (!validateForm()) return

  try {
    const res = await forgetPwdApi({
      clientType: 'web',
      captcha: verificationCode.value,
      password: password.value,
      phoneAreaPrefix: phoneAreaPrefix.value,
      phoneNumber: phoneNumber.value
    })

    if (res.code === 200) {
      showToast('密码修改成功')
      router.push('/login')
    } else {
      showToast(res.msg)
    }
  } catch (error) {
    console.error(error)
  }
}

const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value
}

const toggleConfirmPasswordVisibility = () => {
  showConfirmPassword.value = !showConfirmPassword.value
}

const onAreaCodeConfirm = (item: any) => {
  phoneAreaPrefix.value = item.selectedValues[0]
  showAreaCodePicker.value = false
}
</script>

<style lang="scss" scoped>
.reset-pwd-container {
  min-height: 100vh;
  background-color: #fff;
  padding: 64px 20px 32px;
  box-sizing: border-box;

  .input-group {
    background: transparent;
    border-bottom: 1px solid #ebedf0;
    border-radius: 0;
    margin-bottom: 16px;
    overflow: hidden;

    :deep(.van-field) {
      background: transparent;

      &::after {
        display: none;
      }
    }

    :deep(.van-field__label) {
      width: auto;
      display: flex;
      align-items: center;
    }
  }

  .country-code {
    display: flex;
    align-items: center;
    gap: 4px;
    padding-right: 8px;
    border-right: 1px solid #ebedf0;
    margin-right: 8px;

    span {
      font-size: 14px;
      color: #333;
      font-weight: normal;
    }
  }

  .van-button {
    margin-top: 24px;
  }
}
</style>